import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, Injector, Signal, WritableSignal, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, firstValueFrom } from 'rxjs';
import { v4, v7 } from 'uuid';
import type { AIChatContextGuardStatus, AIInterviewAnswer, AIInterviewResponse } from '@jms/model';
import { AIService } from '@workbuddy/ai/core/ai.service';
import { AiUsageLimitService } from '@workbuddy/ai/core';
import { NativeWorkbuddyAgentChatAdapter } from './native.workbuddy.agent.chat.adapter';
import { AgentUtility } from '../agent.utility';
import { AgentPanelService, NEW_CHAT_DRAFT_KEY } from './agent.panel.service';
import { AgentRevealService } from './agent.reveal.service';
import { BackgroundStreamManager } from './background.stream.manager';
import type { AgentModelTier, AnyPart, CardAction, PendingFile, InterviewCardActionMeta, ThreadLoadFailure } from '../agent.types';
import { coerceThreadModelTier, readPersistedModelTier } from '../agent.types';
import type { UIMessage } from 'ai';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Delay before fetching thread title after first message (allows Mastra generateTitle to complete) */
const TITLE_FETCH_DELAY_MS = 2000;

/** Max poll attempts for the generated title — generateTitle is an async LLM call that can take longer than one delay */
const TITLE_FETCH_MAX_ATTEMPTS = 6;

/** Maximum number of threads to keep in the in-memory message cache */
const MAX_CACHED_THREADS = 20;

/**
 * How long to wait before re-attempting a thread whose message load failed.
 *
 * A failed load leaves the foreground empty, which is exactly the condition the popup uses to decide
 * a thread needs loading — so without a cooldown every panel open/close re-fired the same failing
 * request. When the failure is a rate limit, that feedback loop makes the rate limit worse.
 */
const THREAD_LOAD_RETRY_COOLDOWN_MS = 10_000;

/**
 * Chat service for Agent V2.
 * Wraps NativeWorkbuddyAgentChatAdapter to provide a clean API for chat components.
 * Components never touch the adapter directly — they interact through this service's signals and methods.
 *
 * Provided at component level (one instance per chat container), NOT providedIn root.
 */
@Injectable()
export class AgentChatService {

  private readonly aiService = inject(AIService);
  private readonly usageLimits = inject(AiUsageLimitService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly panelService = inject(AgentPanelService);
  private readonly backgroundManager = inject(BackgroundStreamManager);
  private readonly revealService = inject(AgentRevealService);

  // ─── Configuration (set by chat.container via configure()) ──────────────────
  private $mode!: Signal<'fullscreen' | 'popup'>;
  private $currentJobId!: Signal<string | undefined>;
  private $currentWorkOrderId!: Signal<string | undefined>;
  private $interactionMode!: Signal<'interactive' | 'automation'>;

  // ─── Chat adapter ──────────────────────────────────────────────────────────
  /** Signal holding the current adapter — effects read $chat() to auto re-track on swap. */
  private readonly $chat = signal<NativeWorkbuddyAgentChatAdapter>(null!);
  /** Untracked read for imperative code — avoids polluting calling effects' dependencies. */
  private get chat(): NativeWorkbuddyAgentChatAdapter { return untracked(() => this.$chat()); }
  private readonly threadMessageCache = new Map<string, UIMessage[]>();
  private readonly threadCacheOrder: string[] = [];
  // Per-thread model tier, resolved once from thread metadata and then reused. Clicking
  // back and forth between the same chats must NOT re-fetch the thread each time — that
  // redundant per-switch GET is what bursts the API into 429s. A cache hit publishes the
  // tier synchronously and skips the network call entirely.
  private readonly threadModelTierCache = new Map<string, AgentModelTier | null>();
  // In-flight tier lookups, so the two callers (thread switch and send) share one request instead of
  // racing two. The cache above is only written when a response lands, so without this a send issued
  // straight after switching to a cold thread fires a second identical GET.
  private readonly threadModelTierRequests = new Map<string, Promise<AgentModelTier | null>>();
  // Threads confirmed to hold no messages. Tracked separately from threadMessageCache because that
  // cache is LRU-evicted — recording emptiness here rather than as an empty cache entry means an
  // evicted thread WITH messages still re-fetches, while a genuinely empty one never does.
  private readonly knownEmptyThreadIds = new Set<string>();
  // When a thread's message load last failed, keyed by thread id — see THREAD_LOAD_RETRY_COOLDOWN_MS.
  private readonly failedThreadLoads = new Map<string, ThreadLoadFailure>();
  // Threads whose messages are being fetched right now. An in-flight load leaves the foreground
  // empty, which is indistinguishable from "not loaded yet" to the caller, so this is what stops a
  // slow response being re-requested every time the panel is toggled. A signal because it also
  // drives $threadLoading, which holds sends until the open thread's history has landed.
  private readonly loadingThreadIds = signal<ReadonlySet<string>>(new Set());
  private titleFetchTimer: ReturnType<typeof setTimeout> | null = null;

  // ─── Reasoning state ───────────────────────────────────────────────────────
  private readonly manuallyExpandedBlocks = new Set<string>();

  // ─── Public signals (readonly to consumers) ────────────────────────────────
  public readonly $threadId: WritableSignal<string | null> = signal<string | null>(null);
  public readonly $allSessionBlobUrls: WritableSignal<Record<string, string>> = signal<Record<string, string>>({});
  public readonly $cardActions: WritableSignal<Record<string, InterviewCardActionMeta>> = signal<Record<string, InterviewCardActionMeta>>({});
  public readonly $collapsedActivityBlocks: WritableSignal<Set<string>> = signal<Set<string>>(new Set<string>());

  // The model tier the ACTIVE thread was started on, read back from its stored metadata. null means
  // "no thread-specific tier" — either a brand-new empty chat or a pre-feature thread with no tier
  // recorded — in which case the composer falls back to the user's persisted default. Set on thread
  // restore/switch (from getThread metadata) and on the first send of a new chat; cleared on New Chat.
  public readonly $activeThreadModelTier: WritableSignal<AgentModelTier | null> = signal<AgentModelTier | null>(null);

  public readonly $streamingError = signal<string | null>(null);
  /** Prevents the composer from submitting into a missing or inaccessible conversation. */
  private readonly _threadUnavailable = signal(false);
  public readonly $threadUnavailable = this._threadUnavailable.asReadonly();
  /**
   * True while the open thread's saved history is still loading. Sends wait on it: until the load
   * lands, the thread is not yet known to exist or be accessible, so a turn could go into a missing
   * or denied conversation. Read by the composer to hold Send, and by sendMessage as the real guard.
   */
  public readonly $threadLoading = computed(() => {

    const threadId = this.$threadId();
    return !!threadId && this.loadingThreadIds().has(threadId);
  });
  private readonly _threadLoadError = signal<string | null>(null);
  public readonly $threadLoadError = this._threadLoadError.asReadonly();

  private readonly _expandedTools = signal(new Set<string>());
  public readonly $expandedTools = this._expandedTools.asReadonly();

  // These are initialized with safe defaults and updated in initChat() when the adapter is created
  public readonly $messages = signal<UIMessage[]>([]);
  public readonly $isStreaming = signal(false);
  public readonly $contextGuardStatus = signal<AIChatContextGuardStatus | null>(null);
  /** The model provider itself refused (its own quota/billing/capacity), not our tenancy cap. */
  public readonly $providerUnavailable = signal(false);
  public readonly $threadTitleUpdate = signal<{ threadId: string; title: string } | null>(null);
  public readonly $lastMessageIsUser = computed(() => {

    const m = this.$messages();
    return m.length > 0 && m[m.length - 1].role === 'user';
  });

  /**
   * Configure the service with context signals from the chat container.
   * Must be called before initChat().
   */
  public configure(config: { $mode: Signal<'fullscreen' | 'popup'>; $currentJobId: Signal<string | undefined>; $currentWorkOrderId: Signal<string | undefined>; $interactionMode: Signal<'interactive' | 'automation'> }): void {

    this.$mode = config.$mode;
    this.$currentJobId = config.$currentJobId;
    this.$currentWorkOrderId = config.$currentWorkOrderId;
    this.$interactionMode = config.$interactionMode;
  }

  /**
   * Initialize the chat adapter and set up all reactive effects.
   * Call after configure() and optionally pass a threadId to restore.
   */
  public initChat(threadId?: string): void {

    this.$chat.set(this.createAdapter());

    // Sync public signals from the adapter via effects.
    // $chat() is tracked — effects auto re-track when adapter is swapped via $chat.set().
    effect(() => {

      const msgs = this.$chat().messages;
      this.$messages.set(msgs);
    }, { injector: this.injector });
    effect(() => {

      const status = this.$chat().status;
      this.$isStreaming.set(status === 'streaming' || status === 'submitted');
    }, { injector: this.injector });
    // Sync streaming error from adapter into a dedicated signal
    effect(() => {

      const error = this.$chat().error;
      this.$streamingError.set(error?.message ?? null);
    }, { injector: this.injector });
    effect(() => {

      this.$contextGuardStatus.set(this.$chat().contextGuardStatus);
    }, { injector: this.injector });
    effect(() => {

      this.$providerUnavailable.set(this.$chat().providerUnavailable);
    }, { injector: this.injector });
    // A send refused by the usage-limit door check (429 from a stale browser) is not an error:
    // the adapter rolled the turn back silently; re-reading the status here flips the composer
    // into the blocked notice, which explains what happened and when AI resumes.
    effect(() => {

      if (this.$chat().usageLimitRejections > 0) void this.usageLimits.refresh();
    }, { injector: this.injector });

    // Set up effects
    this.revealService.init(this.$chat);
    this.setupAutoCollapseActivityBlocksEffect();
    this.setupProvisionalTabEffect();

    // Restore thread if provided
    if (threadId) {
      this.$threadId.set(threadId);
      this.loadActiveThreadModelTier(threadId);
      this.aiService.getThreadMessages(AgentUtility.AGENT_ID, threadId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response) => {
          this.chat.messages = AgentUtility.convertToUIMessages(response.uiMessages ?? []);
          this.syncSignalsFromAdapter();
        },
        error: (err) => console.error('[AgentChat] Failed to restore thread:', err),
      });
    }
  }

  /**
   * Clean up the chat adapter and all pending work.
   */
  public dispose(): void {

    if (this.titleFetchTimer) clearTimeout(this.titleFetchTimer);
    this.revealService.reset();
    this.detachCurrentStream();
  }

  /**
   * Detach the current adapter to BackgroundStreamManager so its live state survives this container.
   * Extracted from switchThread/createNewThread/dispose to eliminate duplication.
   *
   * A paused write-tool approval is `idle` (not streaming), but it holds the in-memory runId needed to
   * resume — so it is preserved here alongside active streams. Without this, destroying the container
   * (Automations swap, fullpage nav-away/minimize) would drop the pending approval and force a lossy
   * cold reload; keeping the adapter lets switchThread reattach the exact card, runId intact.
   */
  private detachCurrentStream(): void {

    if (!this.$isStreaming() && !this.chat.hasPendingApproval) return;
    const threadId = this.$threadId();
    if (threadId) {
      this.backgroundManager.detach(threadId, this.chat);
      this.$chat.set(this.createAdapter());
    } else {
      void this.chat.stop();
    }
  }

  /**
   * Cache current messages and detach the streaming adapter to background.
   * Called by chat.container when the popup panel is minimized.
   */
  public detachToBackground(): void {

    const threadId = this.$threadId();
    if (threadId && this.chat.messages.length > 0) {
      this.cacheThreadMessages(threadId, this.chat.messages);
    }
    this.detachCurrentStream();
  }

  /**
   * Rebind public signals from the current chat adapter.
   * Called after setting a new adapter to ensure immediate signal sync.
   */
  private syncSignalsFromAdapter(): void {

    this.$messages.set(this.chat.messages);
    this.$isStreaming.set(this.chat.status === 'streaming' || this.chat.status === 'submitted');
    this.$contextGuardStatus.set(this.chat.contextGuardStatus);
    this.$providerUnavailable.set(this.chat.providerUnavailable);
    this.rebuildThreadState();
  }

  /** "Try again" on the provider-unavailable notice: restore the composer so the user can resend. */
  public dismissProviderUnavailable(): void {

    this.chat.clearProviderUnavailable();
    this.$providerUnavailable.set(false);
  }

  // ─── Messaging ─────────────────────────────────────────────────────────────

  /**
   * Send a message with optional file attachments and metadata.
   */
  // `modelTier` defaults to the user's persisted composer choice so callers that don't pass it
  // explicitly (e.g. retry) still honour the selected tier. Only the tier string ever travels —
  // never a model name.
  public async sendMessage(text: string, files?: PendingFile[], metadata?: Record<string, unknown>, modelTier: AgentModelTier = readPersistedModelTier()): Promise<void> {

    if (this.$threadUnavailable() || this.$threadLoading()) return;

    if (this.$interactionMode() === 'interactive' && this.$contextGuardStatus()?.band === 'blocked') {
      return;
    }

    // Detect first turn before setting threadId
    const isFirstTurn = !this.$threadId();

    // Generate a thread ID on first message so the backend can persist from the start
    if (isFirstTurn) {
      const newThreadId = v7();
      this.$threadId.set(newThreadId);
      // Mirror the chosen tier locally so the composer locks to it immediately. The backend stamps
      // the same tier onto the new thread's metadata on this first turn; seeding the cache here means
      // switching away and back never triggers a getThread for a thread we already know the tier of.
      this.$activeThreadModelTier.set(modelTier);
      this.threadModelTierCache.set(newThreadId, modelTier);
    }

    const threadId = this.$threadId()!;

    // Tier that travels with the turn. A NEW chat uses the composer's pick (already seeded into the
    // cache above). An EXISTING thread's tier was fixed at creation and stored in metadata; the
    // composer only mirrors it and can transiently read 'default' before a cold switch/restore
    // resolves getThread -- so resolve the authoritative tier here and never let a fast send override it.
    const effectiveModelTier = isFirstTurn ? modelTier : ((await this.resolveThreadModelTier(threadId)) ?? modelTier);

    // The lookup above can outlast a switch to another conversation, a fresh history load of this
    // one, or a denial landing for it. Each means the conversation this turn was written for is no
    // longer the open, accessible one, so dispatching would send it into a stale or denied thread.
    if (this.$threadId() !== threadId || this.$threadUnavailable() || this.$threadLoading()) return;

    // This thread is about to hold a message, so it is no longer one we can skip loading.
    this.knownEmptyThreadIds.delete(threadId);

    // Build FileUIPart[] from ready files with blob URLs
    const readyFiles = files?.filter(f => f.status === 'ready') ?? [];
    // Duplicate filename into providerMetadata.workbuddy.filename so it survives Mastra V2's
    // memory round-trip — V2 strips the top-level `filename` field on file parts during
    // persistence/replay, but keeps providerMetadata. Both jms-web and jms-ai fall back to
    // this field when reading file parts from history.
    const fileParts = readyFiles
      .filter(f => f.blobUrl)
      .map(f => {
        const filename = `att_${f.id}__${f.file.name}`;
        return { type: 'file' as const, url: f.blobUrl!, mediaType: f.file.type, filename, providerMetadata: { workbuddy: { filename } } };
      });

    // Merge new blob URLs into cumulative session map
    const newBlobUrls: Record<string, string> = {};
    for (const f of readyFiles) {
      if (f.blobUrl) newBlobUrls[f.id] = f.blobUrl;
    }
    if (Object.keys(newBlobUrls).length > 0) {
      this.$allSessionBlobUrls.update(prev => ({ ...prev, ...newBlobUrls }));
    }

    // Rebuild file list for generative UI cards
    this.rebuildThreadState();

    // Build a UIMessage for the native adapter
    const parts: Array<{ type: string; text?: string; url?: string; mediaType?: string; filename?: string; providerMetadata?: { workbuddy: { filename: string } } }> = [];
    if (text) {
      parts.push({ type: 'text', text });
    }
    for (const fp of fileParts) {
      parts.push(fp);
    }
    const userMessage: UIMessage = { id: v4(), role: 'user', parts: parts as UIMessage['parts'] };

    await this.chat.sendTurn({
      userMessage,
      threadId,
      interactionMode: this.$interactionMode(),
      modelTier: effectiveModelTier,
      currentJobId: this.$currentJobId(),
      currentWorkOrderId: this.$currentWorkOrderId(),
      attachmentBlobUrls: this.$allSessionBlobUrls(),
    });

    // Fetch generated title after first turn (Mastra generateTitle saves to storage after stream ends)
    if (isFirstTurn) {
      this.fetchThreadTitle(threadId);
    }
  }

  /**
   * Stop the active stream.
   */
  public async stopStreaming(): Promise<void> {

    await this.chat.stop();
  }

  // ─── Thread Management ─────────────────────────────────────────────────────

  /**
   * Switch to an existing thread, caching the current one.
   */
  public async switchThread(threadId: string): Promise<void> {

    if (this.titleFetchTimer) clearTimeout(this.titleFetchTimer);
    this._threadLoadError.set(null);
    this._threadUnavailable.set(false);
    this.detachCurrentStream();
    this.chat.resetTransientState();

    // Save current thread's live messages to cache
    const prevThreadId = this.$threadId();
    if (prevThreadId && this.chat.messages.length > 0) {
      this.cacheThreadMessages(prevThreadId, this.chat.messages);
    }

    this.$threadId.set(threadId);
    this.$allSessionBlobUrls.set({});
    // Resolve the thread's stored tier so the composer locks to whatever this chat ran on. Runs for
    // every branch below (reattach / warm cache / cold load) since the tier lives in thread metadata.
    this.loadActiveThreadModelTier(threadId);

    // Check if the target thread is running in background — reattach it
    const reattached = this.backgroundManager.reattach(threadId);
    if (reattached) {
      this.$chat.set(reattached);
      this.backgroundManager.markRead(threadId);
      this.syncSignalsFromAdapter();
      return;
    }

    // Warm cache hit: restore live messages (preserves pending approvals)
    const cached = this.threadMessageCache.get(threadId);
    if (cached) {
      this.chat.messages = cached;
      this.syncSignalsFromAdapter();
      return;
    }

    // Known to hold nothing — re-fetching on every panel reopen would spend requests to learn the
    // same nothing. Threads WITH messages fall through to the cold load when the LRU cache has
    // evicted them, so this shortcut only ever applies to genuinely empty ones.
    if (this.knownEmptyThreadIds.has(threadId)) {
      this.chat.messages = [];
      this.syncSignalsFromAdapter();
      return;
    }

    // A cold target must not inherit the previous thread's messages, including when its load fails.
    // Otherwise New chat can cache those old messages under the unavailable target's ID.
    this.chat.messages = [];
    this.syncSignalsFromAdapter();

    // A load that just failed stays failed for a moment. The caller's "no messages means load it"
    // rule would otherwise retry on every panel toggle, which turns a rate-limit rejection into a
    // stream of further rejections.
    const failure = this.failedThreadLoads.get(threadId);
    if (failure && Date.now() - failure.failedAt < THREAD_LOAD_RETRY_COOLDOWN_MS) {
      this._threadUnavailable.set(failure.unavailable);
      this._threadLoadError.set(failure.unavailable ? null : 'Could not load this conversation. Please try again.');
      return;
    }

    // Already being fetched. The foreground stays empty for as long as the request is in flight,
    // which is the same "no messages" condition the popup reads as "needs loading" — so without this
    // guard, toggling the panel during a slow or throttled response fires a duplicate load each time.
    // The pending request populates the view when it lands, so there is nothing to do but wait.
    if (untracked(() => this.loadingThreadIds()).has(threadId)) return;

    // Cold load: fetch from backend storage. The loading mark lasts exactly as long as the request,
    // whichever way it ends, so sends for this thread are held until its outcome is known.
    this.loadingThreadIds.update(ids => new Set(ids).add(threadId));
    this.aiService
      .getThreadMessages(AgentUtility.AGENT_ID, threadId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loadingThreadIds.update(ids => new Set([...ids].filter(id => id !== threadId)))),
      )
      .subscribe({
        next: (response) => {
          const messages = AgentUtility.convertToUIMessages(response.uiMessages ?? []);
          this.failedThreadLoads.delete(threadId);
          if (messages.length > 0) {
            this.knownEmptyThreadIds.delete(threadId);
            this.cacheThreadMessages(threadId, messages);
          } else {
            this.knownEmptyThreadIds.add(threadId);
          }

          // Record the result above regardless, but only paint it if this is still the open thread —
          // a slow response for a thread the user has since left must not overwrite what they are
          // now looking at.
          if (this.$threadId() !== threadId) return;
          this.chat.messages = messages;
          this.syncSignalsFromAdapter();
        },
        error: (err) => {
          console.error('[AgentChat] Failed to load thread messages:', err);
          const unavailable = err instanceof HttpErrorResponse && (err.status === 403 || err.status === 404);
          this.failedThreadLoads.set(threadId, {
            failedAt: Date.now(),
            unavailable,
          });
          if (this.$threadId() !== threadId) return;
          this._threadUnavailable.set(unavailable);
          this._threadLoadError.set(unavailable ? null : 'Could not load this conversation. Please try again.');
        },
      });
  }

  /**
   * Start a new empty thread, caching the current one.
   */
  public async createNewThread(): Promise<void> {

    this._threadLoadError.set(null);
    this._threadUnavailable.set(false);

    if (this.titleFetchTimer) clearTimeout(this.titleFetchTimer);
    this.detachCurrentStream();
    this.chat.resetTransientState();

    // Save current thread's live messages before starting fresh
    const prevThreadId = this.$threadId();
    if (prevThreadId && this.chat.messages.length > 0) {
      this.cacheThreadMessages(prevThreadId, this.chat.messages);
    }

    this.$threadId.set(null);
    this.chat.messages = [];
    this.$messages.set([]);
    this.$allSessionBlobUrls.set({});
    this.$cardActions.set({});
    // The new chat shares the draft of the thread you came from, so what you were typing carries
    // into it; other threads keep their own. A truly fresh new chat (no prior thread) starts empty.
    this.panelService.setComposerDraft(NEW_CHAT_DRAFT_KEY, prevThreadId ? this.panelService.composerDraftFor(prevThreadId) : '');
    // New empty chat: drop any thread-specific tier so the composer unlocks and follows the user's default.
    this.$activeThreadModelTier.set(null);
  }

  // ─── Tool Approvals (HITL) ─────────────────────────────────────────────────

  /**
   * Approve a tool call by its approval ID. For the `interview` tool, pass the
   * structured card answers as `interviewResponse` so the resumed execute can
   * return them as the tool result instead of the model re-asking.
   * Resolves `false` when the approval could not be dispatched (e.g. its run id
   * is unresolvable after a restore), in which case an error is surfaced for retry.
   */
  public async approveToolCall(toolCallId: string, interviewResponse?: AIInterviewResponse): Promise<boolean> {

    return this.chat.approveToolCall(toolCallId, interviewResponse);
  }

  /**
   * Decline a tool call by its approval ID with an optional reason. Resolves `false` when the
   * approval could not be dispatched (e.g. its run id is unresolvable after a restore).
   */
  public async declineToolCall(toolCallId: string, reason?: string): Promise<boolean> {

    return this.chat.declineToolCall(toolCallId, reason);
  }

  /**
   * Record a card action (approved/declined/selected/skipped) against the tool part.
   *
   * In-memory only. The card resumes through the approve path, which drives the tool part to
   * output-available (or output-denied) with the answers on it — and that stored part, not this
   * metadata, is what the message list reads back when a thread is reloaded. Writing the same
   * outcome to the server as well cost a second request per card and told us nothing new.
   *
   * The local write still matters for the live window before the resumed output lands: it gates the
   * card overlay so an answered card retires immediately, and it supplies the positional fallback
   * recap until the real output arrives.
   */
  public persistCardAction(messageId: string, toolCallId: string, meta: InterviewCardActionMeta): void {

    this.$cardActions.update(prev => ({ ...prev, [toolCallId]: meta }));

    const message = this.chat.messages.find(m => m.id === messageId && m.role === 'assistant');
    if (!message) return;

    const partIndex = message.parts.findIndex(p => AgentUtility.partField(p, 'toolCallId', '') === toolCallId);
    if (partIndex === -1) return;

    const originalPart = message.parts[partIndex] as Record<string, unknown>;
    const existing = (originalPart['callProviderMetadata'] as Record<string, unknown>) ?? {};
    message.parts[partIndex] = { ...originalPart, callProviderMetadata: { ...existing, workbuddy: { cardAction: meta.action, timestamp: meta.timestamp, answers: meta.answers } } } as unknown as typeof message.parts[number];
  }

  // ─── Reasoning State ───────────────────────────────────────────────────────

  /**
   * Toggle manual expand/collapse of an activity block.
   */
  public toggleActivityBlock(blockKey: string): void {

    const isCurrentlyCollapsed = this.$collapsedActivityBlocks().has(blockKey);
    if (isCurrentlyCollapsed) {
      // User is expanding — track it so auto-collapse won't override
      this.manuallyExpandedBlocks.add(blockKey);
    } else {
      // User is collapsing — remove from manual tracking
      this.manuallyExpandedBlocks.delete(blockKey);
    }
    this.$collapsedActivityBlocks.update(prev => {
      const next = new Set(prev);
      if (next.has(blockKey)) {
        next.delete(blockKey);
      } else {
        next.add(blockKey);
      }
      return next;
    });
  }

  // ─── Tool Expand State ─────────────────────────────────────────────────────

  /**
   * Toggle expand/collapse of a tool call result section.
   */
  public toggleToolExpand(callId: string): void {

    this._expandedTools.update(prev => {
      const next = new Set(prev);
      if (next.has(callId)) {
        next.delete(callId);
      } else {
        next.add(callId);
      }
      return next;
    });
  }

  // ─── Error Management ─────────────────────────────────────────────────────

  /**
   * Clear all error states.
   */
  public clearErrors(): void {

    this.$streamingError.set(null);
    this._threadLoadError.set(null);
    this.chat?.clearError();
  }

  /**
   * Retry the last user message after a streaming error.
   * Clears the error, removes the failed user/assistant messages, and re-sends.
   */
  public async retryLastMessage(): Promise<void> {

    const messages = this.$messages();
    const lastUserIndex = messages.map(m => m.role).lastIndexOf('user');
    if (lastUserIndex === -1) return;

    const lastUserMsg = messages[lastUserIndex];

    // Extract text from the user message parts
    const text = lastUserMsg.parts.filter((p): p is { type: 'text'; text: string } => p.type === 'text').map(p => p.text).join('\n');
    if (!text) return;

    // Remove the last user message and any subsequent messages (failed assistant response)
    // to avoid duplicates when sendMessage adds the user message back
    this.chat.messages = messages.slice(0, lastUserIndex);

    // Clear the streaming error and re-send
    this.$streamingError.set(null);
    this.chat?.clearError();
    await this.sendMessage(text);
  }

  // ─── Private: Effects ──────────────────────────────────────────────────────

  /**
   * Auto-collapse activity blocks when a text part appears after them.
   */
  private setupAutoCollapseActivityBlocksEffect(): void {

    effect(() => {

      const chat = this.$chat();
      const _status = chat.status;
      const keysToCollapse = new Set<string>();

      for (const msg of chat.messages) {
        if (msg.role !== 'assistant') continue;
        const items = AgentUtility.groupMessageParts(msg.parts as AnyPart[]);
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind !== 'activity-block') continue;
          const blockKey = AgentUtility.activityBlockKey(msg.id, item.blockIndex);
          const hasTextAfter = items.slice(i + 1).some(it => it.kind === 'text');
          if (hasTextAfter) keysToCollapse.add(blockKey);
        }
      }

      if (keysToCollapse.size > 0) {
        this.$collapsedActivityBlocks.update(prev => {
          const next = new Set(prev);
          let changed = false;
          for (const k of keysToCollapse) {
            if (!next.has(k) && !this.manuallyExpandedBlocks.has(k)) { next.add(k); changed = true; }
          }
          return changed ? next : prev;
        });
      }
    }, { injector: this.injector });
  }

  /**
   * In popup mode, create a tab as soon as the AI starts streaming a response.
   * This gives immediate visual feedback without waiting for the server to generate a title.
   */
  private setupProvisionalTabEffect(): void {

    effect(() => {
      const streaming = this.$isStreaming();
      if (!streaming || this.$mode() !== 'popup') return;

      const threadId = untracked(() => this.$threadId());
      if (!threadId) return;

      const hasTab = untracked(() => this.panelService.$openTabs().some(t => t.threadId === threadId));
      if (hasTab) return;

      const firstMsg = untracked(() => this.chat.messages.find(m => m.role === 'user'));
      const text = firstMsg?.parts?.find((p: unknown) => (p as { type: string }).type === 'text') as { text?: string } | undefined;
      const label = text?.text ?? 'New chat';
      const provisionalTitle = label.length > 40 ? label.slice(0, 40) + '...' : label;
      this.panelService.addTab(threadId, provisionalTitle);
    }, { injector: this.injector });
  }

  /**
   * Create a new adapter instance.
   */
  private createAdapter(): NativeWorkbuddyAgentChatAdapter {

    return new NativeWorkbuddyAgentChatAdapter(this.aiService);
  }

  /**
   * Fetch thread title after first message.
   * Mastra's generateTitle saves to storage but doesn't emit stream events,
   * so we fetch the title after a delay to allow the async generation to complete.
   */
  private fetchThreadTitle(threadId: string, attemptsLeft: number = TITLE_FETCH_MAX_ATTEMPTS): void {

    this.titleFetchTimer = setTimeout(async () => {
      // generateTitle is an async LLM call with no stream event and can take longer than one delay.
      // Poll until the title lands so the side panel updates live instead of only after a page refresh.
      try {
        if (this.$threadId() !== threadId) return; // Stale fetch guard — user moved to another thread

        const thread = await firstValueFrom(this.aiService.getThread(AgentUtility.AGENT_ID, threadId));
        if (thread.title) {
          if (this.$mode() === 'popup') {
            this.panelService.updateTabTitle(threadId, thread.title);
          }
          this.$threadTitleUpdate.set({ threadId, title: thread.title });
          return;
        }

        if (attemptsLeft > 1) this.fetchThreadTitle(threadId, attemptsLeft - 1);
      } catch {
        if (attemptsLeft > 1) this.fetchThreadTitle(threadId, attemptsLeft - 1);
      }
    }, TITLE_FETCH_DELAY_MS);
  }

  /**
   * Read the model tier the given thread was started on (from its stored metadata) and publish it
   * as $activeThreadModelTier so the composer can lock to it. A pre-feature thread (or any thread
   * with no recorded tier) leaves the signal null, so the composer falls back to the user's default.
   * Stale-guarded: a fetch that resolves after the user has switched threads is discarded.
   */
  private loadActiveThreadModelTier(threadId: string): void {

    // Cache hit: publish synchronously, no network call. This is the key guard that stops
    // rapid chat switching from firing a redundant getThread per click (the 429 storm).
    if (this.threadModelTierCache.has(threadId)) {
      this.$activeThreadModelTier.set(this.threadModelTierCache.get(threadId) ?? null);
      return;
    }

    void this.resolveThreadModelTier(threadId).then(tier => {
      if (this.$threadId() !== threadId) return; // Stale fetch guard — user moved to another thread
      this.$activeThreadModelTier.set(tier);
    });
  }

  /**
   * Resolve the authoritative model tier an existing thread was started on, awaiting the lookup on a
   * cache miss. Used at send time so a message can never go out on the composer's transient 'default'
   * during the cold switch/restore window before the tier has loaded. Returns null for a pre-feature
   * thread (no stored tier), letting the caller fall back to the composer value.
   *
   * Concurrent callers share one request: the cache is only written when a response lands, so a send
   * issued while the switch's lookup is still in flight would otherwise fire a duplicate GET.
   */
  private resolveThreadModelTier(threadId: string): Promise<AgentModelTier | null> {

    if (this.threadModelTierCache.has(threadId)) {
      return Promise.resolve(this.threadModelTierCache.get(threadId) ?? null);
    }

    const existing = this.threadModelTierRequests.get(threadId);
    if (existing) return existing;

    const request = firstValueFrom(this.aiService.getThread(AgentUtility.AGENT_ID, threadId))
      .then(thread => {
        const tier = coerceThreadModelTier(thread.metadata?.['modelTier']);
        this.threadModelTierCache.set(threadId, tier);
        return tier;
      })
      // Non-fatal: if the thread can't be read, leave the tier unresolved and fall back to default.
      .catch(() => null)
      .finally(() => this.threadModelTierRequests.delete(threadId));

    this.threadModelTierRequests.set(threadId, request);
    return request;
  }

  // ─── Private: Helpers ──────────────────────────────────────────────────────

  /**
   * Cache thread messages with LRU eviction.
   * Keeps at most MAX_CACHED_THREADS entries, evicting the least recently used.
   */
  private cacheThreadMessages(threadId: string, messages: UIMessage[]): void {

    // Remove from current position in order (if exists)
    const idx = this.threadCacheOrder.indexOf(threadId);
    if (idx !== -1) this.threadCacheOrder.splice(idx, 1);

    // Add to end (most recently used)
    this.threadCacheOrder.push(threadId);
    this.threadMessageCache.set(threadId, messages);

    // Evict oldest if over limit
    while (this.threadCacheOrder.length > MAX_CACHED_THREADS) {
      const evictId = this.threadCacheOrder.shift()!;
      this.threadMessageCache.delete(evictId);
    }
  }

  /**
   * Rebuilds all thread-level derived state from loaded messages in a single pass:
   * card action states (from callProviderMetadata) and collapsed thinking block keys.
   */
  private rebuildThreadState(): void {

    const cardActions: Record<string, InterviewCardActionMeta> = {};

    for (const message of this.chat.messages) {
      for (let i = 0; i < message.parts.length; i++) {
        const part = message.parts[i];

        if (message.role !== 'assistant') continue;

        const toolCallId = AgentUtility.partField(part, 'toolCallId', '');
        if (toolCallId) {
          const providerMeta = AgentUtility.partField<Record<string, unknown> | null>(part, 'callProviderMetadata', null);
          const wbMeta = providerMeta?.workbuddy as { cardAction?: string; timestamp?: number; answers?: AIInterviewAnswer[] } | undefined;
          if (wbMeta?.cardAction) {
            cardActions[toolCallId] = {
              action: wbMeta.cardAction as CardAction,
              timestamp: wbMeta.timestamp ?? 0,
              answers: wbMeta.answers ?? [],
            };
          }
        }
      }
    }

    // Collapse blocks the agent has moved beyond, mirroring the live auto-collapse
    // effect's rule. The trailing block of the last message stays OPEN while the
    // stream is active (it is still being streamed into) — without this, reattaching
    // a backgrounded stream (popup reopen, nav back to /agent) collapses the live
    // block mid-turn. Manual expand/collapse choices are preserved across the rebuild.
    const messages = this.chat.messages;
    const lastMessage = messages[messages.length - 1];
    const streaming = this.$isStreaming();
    const previouslyCollapsed = this.$collapsedActivityBlocks();
    const blockKeys = new Set<string>();
    for (const message of messages) {
      if (message.role !== 'assistant') continue;
      const items = AgentUtility.groupMessageParts(message.parts as AnyPart[]);
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind !== 'activity-block') continue;
        const blockKey = AgentUtility.activityBlockKey(message.id, item.blockIndex);
        if (this.manuallyExpandedBlocks.has(blockKey)) continue;
        const hasTextAfter = items.slice(i + 1).some(it => it.kind === 'text');
        const isLiveTrailingBlock = streaming && message === lastMessage && !hasTextAfter;
        // A live block the user explicitly collapsed stays collapsed (key is in the previous set).
        if (isLiveTrailingBlock && !previouslyCollapsed.has(blockKey)) continue;
        blockKeys.add(blockKey);
      }
    }

    this.$cardActions.set(cardActions);
    if (blockKeys.size > 0) {
      this.$collapsedActivityBlocks.set(blockKeys);
    }
  }
}
